import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { useAuth } from "../../context/AuthContext";
import { toast, Toaster } from "sonner";
import { KeyRound, Eye, EyeOff, Copy, AlertTriangle } from "lucide-react";

/**
 * Super-admin-only page (Mod 2).
 * Lists 3rd-party API keys configured via backend .env. Values are masked by
 * default; super-admin can reveal one at a time. Read-only — to update a key,
 * edit /app/backend/.env and restart the backend.
 */
export default function AdminApiKeys() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [keys, setKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [revealed, setRevealed] = useState({});

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/api-keys");
            setKeys(data.keys || []);
        } catch (e) {
            if (e.response?.status === 403) {
                toast.error("Super-admin only");
                navigate("/admin", { replace: true });
            }
        } finally { setLoading(false); }
    };

    useEffect(() => {
        if (user && user.role !== "super_admin") {
            toast.error("Super-admin only");
            navigate("/admin", { replace: true });
            return;
        }
        load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const reveal = async (k) => {
        if (revealed[k.key]) {
            setRevealed(r => { const n = { ...r }; delete n[k.key]; return n; });
            return;
        }
        try {
            const { data } = await api.get(`/admin/api-keys/${k.key}/reveal`);
            setRevealed(r => ({ ...r, [k.key]: data.value || "" }));
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to reveal");
        }
    };

    const copy = async (val) => {
        try {
            await navigator.clipboard.writeText(val);
            toast.success("Copied to clipboard");
        } catch {
            toast.error("Copy failed");
        }
    };

    const grouped = keys.reduce((acc, k) => {
        (acc[k.category] = acc[k.category] || []).push(k);
        return acc;
    }, {});

    return (
        <AdminLayout>
            <Toaster theme="dark" richColors position="top-center" />
            <div className="p-4 sm:p-8 max-w-4xl">
                <div className="flex items-center gap-2">
                    <KeyRound className="w-5 h-5 text-[#00FF66]" />
                    <h1 className="font-display text-3xl font-bold uppercase text-white">API Keys</h1>
                </div>
                <p className="text-slate-400 text-sm mt-1">All third-party keys configured for this deployment. <span className="text-[#00FF66] font-bold">Super-admin only.</span></p>

                <div className="mt-4 bg-amber-500/10 border border-amber-500/40 rounded-lg p-3 flex items-start gap-2" data-testid="apikeys-warning">
                    <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                    <div className="text-[11px] text-amber-200">
                        Read-only view. To rotate a key, update <code className="bg-slate-900 px-1 rounded">/app/backend/.env</code> and restart the backend.
                        Never paste these values into the player-facing UI or share over chat.
                    </div>
                </div>

                {loading ? (
                    <div className="mt-8 text-slate-500 text-sm">Loading…</div>
                ) : (
                    <div className="mt-6 space-y-6">
                        {Object.entries(grouped).map(([cat, items]) => (
                            <div key={cat} data-testid={`apikeys-category-${cat.toLowerCase()}`}>
                                <h2 className="font-display text-lg font-bold uppercase text-white mb-3">{cat}</h2>
                                <div className="space-y-2">
                                    {items.map((k) => {
                                        const fullVal = revealed[k.key];
                                        return (
                                            <div key={k.key} className="bg-slate-900 border border-slate-800 rounded-lg p-4" data-testid={`apikey-${k.key}`}>
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="min-w-0 flex-1">
                                                        <div className="text-sm font-bold text-white">{k.label}</div>
                                                        <div className="text-[10px] text-slate-500 uppercase tracking-widest mt-0.5">{k.key}</div>
                                                    </div>
                                                    <div className="flex items-center gap-2 flex-shrink-0">
                                                        {k.is_set ? (
                                                            <span className="text-[10px] uppercase tracking-widest text-[#00FF66] font-bold">Configured</span>
                                                        ) : (
                                                            <span className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">Not set</span>
                                                        )}
                                                    </div>
                                                </div>
                                                {k.is_set && (
                                                    <div className="mt-3 flex items-center gap-2">
                                                        <div className="flex-1 bg-slate-950 border border-slate-700 rounded px-3 py-2 font-mono text-xs text-slate-300 break-all"
                                                            data-testid={`apikey-value-${k.key}`}>
                                                            {fullVal !== undefined ? fullVal : (k.preview || "—")}
                                                        </div>
                                                        {!k.readonly && (
                                                            <button onClick={() => reveal(k)} title={fullVal !== undefined ? "Hide" : "Reveal full key"}
                                                                data-testid={`reveal-${k.key}`}
                                                                className="inline-flex items-center justify-center w-9 h-9 rounded border border-slate-700 text-slate-400 hover:text-white hover:bg-slate-800">
                                                                {fullVal !== undefined ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                            </button>
                                                        )}
                                                        {fullVal !== undefined && (
                                                            <button onClick={() => copy(fullVal)} title="Copy"
                                                                data-testid={`copy-${k.key}`}
                                                                className="inline-flex items-center justify-center w-9 h-9 rounded border border-slate-700 text-slate-400 hover:text-[#00FF66] hover:bg-slate-800">
                                                                <Copy className="w-4 h-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}
